import React from 'react';
import { Calendar, ClipboardList, CheckCircle2, FileSpreadsheet } from 'lucide-react';

// Reactor Core - The pulsating center
export const ReactorCore = () => {
  return (
    <div className="relative w-56 h-56 flex items-center justify-center">
      <div className="absolute inset-0 rounded-full border-2 border-cyan-500/30 animate-[spin_20s_linear_infinite]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-cyan-400 rounded-full" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1 w-2 h-2 bg-cyan-400 rounded-full" />
      </div>
      <div className="absolute inset-4 rounded-full border border-cyan-500/20 animate-[spin_15s_linear_infinite_reverse]">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1 w-1.5 h-1.5 bg-cyan-400/60 rounded-full" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1 w-1.5 h-1.5 bg-cyan-400/60 rounded-full" />
      </div>
      <div className="absolute inset-8 rounded-full bg-gradient-to-br from-cyan-950/80 to-slate-900/80 border border-cyan-500/40 animate-[pulse_2s_ease-in-out_infinite] shadow-[0_0_30px_rgba(0,240,255,0.3),inset_0_0_20px_rgba(0,240,255,0.1)]">
        <div className="absolute inset-0 flex items-center justify-center">
          <img 
            src="/hotforge-logo.png" 
            alt="Hotforge" 
            className="w-28 h-28 object-contain opacity-90"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center text-cyan-400 font-black text-4xl tracking-tighter">
            HF
          </div>
        </div>
      </div>
      <div className="absolute inset-0 rounded-full bg-cyan-500/5 blur-xl animate-[pulse_2s_ease-in-out_infinite]" />
    </div>
  );
};

// Orbital Node - Navigation bubble
export const OrbitalNode = ({ 
  icon: Icon, label, badge, badgeColor = 'red',
  onClick, angle, distance, size = 'primary', isExpanded, testId
}) => {
  const sizeClasses = { primary: 'w-36 h-36', secondary: 'w-28 h-28', utility: 'w-20 h-20' };
  const iconSizes = { primary: 'w-10 h-10', secondary: 'w-7 h-7', utility: 'w-5 h-5' };
  const labelSizes = { primary: 'text-sm mt-2 font-semibold', secondary: 'text-xs mt-1.5 font-medium', utility: 'text-[11px] mt-1' };

  const rad = (angle * Math.PI) / 180;
  const x = Math.cos(rad) * distance;
  const y = Math.sin(rad) * distance;

  const badgeColors = { red: 'bg-red-500', orange: 'bg-orange-500', blue: 'bg-blue-500', green: 'bg-green-500', purple: 'bg-purple-500' };

  return (
    <div 
      className="absolute transition-all duration-500 ease-out"
      style={{ transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`, left: '50%', top: '50%' }}
    >
      <button onClick={onClick} data-testid={testId}
        className={`group relative ${sizeClasses[size]} rounded-full bg-slate-900/80 backdrop-blur-xl border border-cyan-500/20 hover:border-cyan-400/60 hover:scale-110 transition-all duration-300 ease-out shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_30px_rgba(0,240,255,0.3)] flex flex-col items-center justify-center ${isExpanded ? 'border-cyan-400 scale-105 shadow-[0_0_25px_rgba(0,240,255,0.4)]' : ''}`}>
        <Icon className={`${iconSizes[size]} text-cyan-400/70 group-hover:text-cyan-300 transition-colors`} />
        <span className={`${labelSizes[size]} text-slate-400 group-hover:text-white font-medium uppercase tracking-wider transition-colors text-center px-1 leading-tight`}>
          {label}
        </span>
        {badge && (
          <div className={`absolute -top-1 -right-1 ${badgeColors[badgeColor]} text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center shadow-lg`}>
            {badge}
          </div>
        )}
        <div className="absolute inset-0 rounded-full bg-cyan-500/0 group-hover:bg-cyan-500/10 transition-colors" />
      </button>
    </div>
  );
};

// Connection Line SVG
export const ConnectionLine = ({ angle, distance, coreRadius = 112 }) => {
  const rad = (angle * Math.PI) / 180;
  const startX = Math.cos(rad) * coreRadius;
  const startY = Math.sin(rad) * coreRadius;
  const endX = Math.cos(rad) * (distance - 56);
  const endY = Math.sin(rad) * (distance - 56);

  return (
    <line x1={startX} y1={startY} x2={endX} y2={endY}
      stroke="rgba(0, 240, 255, 0.4)" strokeWidth="1.5" strokeDasharray="4 4"
      className="animate-[dash_20s_linear_infinite]" />
  );
};

// Production Submenu
export const ProductionSubmenu = ({ isOpen, onNavigate, parentAngle, parentDistance }) => {
  const subItems = [
    { label: 'Schedule', icon: Calendar, action: 'schedule' },
    { label: 'Outstanding', icon: ClipboardList, action: 'outstanding' },
    { label: 'Completed', icon: CheckCircle2, action: 'completed' },
    { label: 'Extract', icon: FileSpreadsheet, action: 'extract' }
  ];

  if (!isOpen) return null;

  return (
    <>
      {subItems.map((item, idx) => {
        const subAngle = parentAngle - 55 + (idx * 25);
        const subDistance = parentDistance + 160;
        return (
          <OrbitalNode key={item.action} icon={item.icon} label={item.label}
            angle={subAngle} distance={subDistance} size="secondary"
            onClick={() => onNavigate(item.action)} testId={`submenu-${item.action}`} />
        );
      })}
    </>
  );
};

// Status Bar Component
export const StatusBar = ({ stats }) => {
  const formatDueDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    const formatted = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    if (diffDays < 0) return `${formatted} (${Math.abs(diffDays)}d overdue)`;
    if (diffDays === 0) return `${formatted} (Today)`;
    if (diffDays === 1) return `${formatted} (Tomorrow)`;
    return `${formatted} (${diffDays}d)`;
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-sm border-b border-cyan-500/20">
      <div className="container mx-auto px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-8 text-sm font-mono">
          <div className="flex items-center gap-2">
            <span className="text-slate-500 uppercase text-xs">Active Orders:</span>
            <span className="text-cyan-400 font-bold">{stats.activeOrders}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 uppercase text-xs">Quality Alerts:</span>
            <span className={`font-bold ${stats.qualityAlerts > 0 ? 'text-red-400' : 'text-green-400'}`}>
              {stats.qualityAlerts}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 uppercase text-xs">Next Delivery:</span>
            {stats.nextDeliveryDetails ? (
              <div className="flex items-center gap-3">
                <span className="text-white font-bold">{stats.nextDeliveryDetails.orderNumber}</span>
                <span className="text-slate-400">|</span>
                <span className="text-cyan-300 text-xs">{stats.nextDeliveryDetails.partNumber}</span>
                <span className="text-slate-500 text-xs max-w-[200px] truncate">{stats.nextDeliveryDetails.description}</span>
                <span className="text-slate-400">|</span>
                <span className={`text-xs font-medium ${
                  stats.nextDeliveryDetails.isOverdue ? 'text-red-400' : 
                  stats.nextDeliveryDetails.daysUntilDue <= 1 ? 'text-orange-400' : 'text-green-400'
                }`}>
                  {formatDueDate(stats.nextDeliveryDetails.dueDate)}
                </span>
              </div>
            ) : (
              <span className="text-slate-400">None</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span>SYSTEM ONLINE</span>
          </div>
        </div>
      </div>
    </div>
  );
};
